/**
 * Search Filters Component for v2v2 SWFL Farmers Market Application
 * Filter sidebar/panel with category, season, location, and vendor type filters
 */



import { useState, useCallback } from 'react';
import { Filter, X, ChevronDown, ChevronUp, Leaf, CalendarDays, MapPin, Store } from 'lucide-react';
import { useSearch } from '@/hooks/use-search';
import { cn } from '@/lib/utils';

interface SearchFiltersProps {
  className?: string;
  variant?: 'sidebar' | 'panel' | 'accordion';
  collapsible?: boolean;
}

export function SearchFilters({
  className,
  variant = 'sidebar',
  collapsible = true,
}: SearchFiltersProps) {
  const {
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    activeFilterCount,
    filterOptions,
  } = useSearch();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    categories: true,
    seasons: true,
    locations: true,
    vendorTypes: false,
  });

  const toggleSection = useCallback((section: string) => {
    if (!collapsible) return;
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, [collapsible]);

  // Variant styles
  const containerStyles = {
    sidebar: 'w-64 flex-shrink-0 space-y-6',
    panel: 'space-y-4 p-4 border rounded-lg',
    accordion: 'space-y-2',
  };

  // Section styles
  const sectionStyles = {
    sidebar: 'space-y-4',
    panel: 'space-y-3',
    accordion: 'border rounded-lg overflow-hidden',
  };

  return (
    <div className={cn(containerStyles[variant], className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <h3 className="font-semibold">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Category Filter */}
      <FilterSection
        title="Category"
        icon={<Leaf className="h-4 w-4" />}
        isOpen={openSections.categories}
        onToggle={() => toggleSection('categories')}
        variant={variant}
        count={filters.categories?.length}
      >
        <CheckboxGroup
          options={filterOptions.categories}
          selected={filters.categories || []}
          onChange={(values) => updateFilter('categories', values)}
        />
      </FilterSection>

      {/* Season Filter */}
      <FilterSection
        title="Season"
        icon={<CalendarDays className="h-4 w-4" />}
        isOpen={openSections.seasons}
        onToggle={() => toggleSection('seasons')}
        variant={variant}
        count={filters.seasons?.length}
      >
        <CheckboxGroup
          options={filterOptions.seasons}
          selected={filters.seasons || []}
          onChange={(values) => updateFilter('seasons', values)}
          labels={{
            'spring': 'Spring (Mar-May)',
            'summer': 'Summer (Jun-Aug)',
            'fall': 'Fall (Sep-Nov)',
            'winter': 'Winter (Dec-Feb)',
            'year-round': 'Year-round',
          }}
        />
      </FilterSection>

      {/* Location Filter */}
      <FilterSection
        title="Location"
        icon={<MapPin className="h-4 w-4" />}
        isOpen={openSections.locations}
        onToggle={() => toggleSection('locations')}
        variant={variant}
        count={filters.locations?.length}
      >
        <CheckboxGroup
          options={filterOptions.locations}
          selected={filters.locations || []}
          onChange={(values) => updateFilter('locations', values)}
        />
      </FilterSection>

      {/* Vendor Type Filter */}
      <FilterSection
        title="Vendor Type"
        icon={<Store className="h-4 w-4" />}
        isOpen={openSections.vendorTypes}
        onToggle={() => toggleSection('vendorTypes')}
        variant={variant}
        count={filters.vendorTypes?.length}
      >
        <CheckboxGroup
          options={['Farm', 'Restaurant', 'Cafe', 'Market', 'Specialty Food', 'Bakery']}
          selected={filters.vendorTypes || []}
          onChange={(values) => updateFilter('vendorTypes', values)}
        />
      </FilterSection>
    </div>
  );
}

// Filter Section Component
interface FilterSectionProps {
  title: string;
  icon?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  variant: 'sidebar' | 'panel' | 'accordion';
  count?: number;
  children: React.ReactNode;
}

function FilterSection({
  title,
  icon,
  isOpen,
  onToggle,
  variant,
  count,
  children,
}: FilterSectionProps) {
  const headerStyles = {
    sidebar: 'flex items-center justify-between cursor-pointer py-2 border-b',
    panel: 'flex items-center justify-between cursor-pointer',
    accordion: 'flex items-center justify-between px-3 py-2 bg-muted/50',
  };

  return (
    <div className={variant === 'accordion' ? 'border rounded-lg overflow-hidden' : ''}>
      <div
        className={cn(headerStyles[variant])}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium">{title}</span>
          {count !== undefined && count > 0 && (
            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
              {count}
            </span>
          )}
        </div>
        {variant !== 'panel' && (
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        )}
      </div>
      {(isOpen || variant === 'panel') && (
        <div className={cn(
          variant === 'sidebar' ? 'pt-3' : 'p-3',
          variant === 'accordion' && 'p-3'
        )}>
          {children}
        </div>
      )}
    </div>
  );
}

// Checkbox Group Component
interface CheckboxGroupProps {
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  labels?: Record<string, string>;
}

function CheckboxGroup({
  options,
  selected,
  onChange,
  labels = {},
}: CheckboxGroupProps) {
  const handleChange = useCallback((value: string, checked: boolean) => {
    if (checked) {
      onChange([...selected, value]);
    } else {
      onChange(selected.filter(v => v !== value));
    }
  }, [selected, onChange]);

  if (options.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No options available
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {options.map((option) => (
        <label
          key={option}
          className="flex items-center gap-2 cursor-pointer group"
        >
          <input
            type="checkbox"
            checked={selected.includes(option)}
            onChange={(e) => handleChange(option, e.target.checked)}
            className="rounded border-input bg-background text-primary ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
          />
          <span className="text-sm group-hover:text-foreground transition-colors">
            {labels[option] || option}
          </span>
        </label>
      ))}
    </div>
  );
}

